import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useMapData } from "@/providers";
import {
  getGeneratedPageDataConfigError,
  resolveGeneratedPageDataSource,
} from "./dataSource";
import { resolveGeneratedPage } from "./registry";

interface IGeneratedPageHostProps {
  data?: any;
}

interface IMockState {
  data: Record<string, any> | null;
  isLoading: boolean;
  error: Error | null;
}

const renderState = (title: string, description: string) => {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-[24px] text-center">
      <p className="text-[18px] font-[700] leading-[25px] text-[#222]">{title}</p>
      <p className="mt-[8px] text-[14px] leading-[20px] text-[#999]">{description}</p>
    </div>
  );
};

const GeneratedPageHost: React.FC<IGeneratedPageHostProps> = ({ data }) => {
  const location = useLocation();
  // 根路径（无 hash 访问 index.html）直接复用 callouts 路由渲染列表，
  // 不跳转、不改 URL，避免 Navigate 二次渲染造成的白屏闪烁。
  // callouts 组件在 pathname 不含 /callouts/{map} 时本就渲染列表，二者一致。
  const effectivePathname = location.pathname === "/" ? "/callouts" : location.pathname;
  const page = resolveGeneratedPage(effectivePathname);
  const pageDataConfig = page?.data;
  const pageId = page?.pageId ?? location.pathname;
  const mockSearchParam = new URLSearchParams(location.search).get("mock");
  const source = resolveGeneratedPageDataSource(pageDataConfig, mockSearchParam);
  const configError = data === undefined ? getGeneratedPageDataConfigError(source, pageDataConfig) : null;
  const remoteKeys = data === undefined && source === "remote" ? pageDataConfig?.dataKeys ?? [] : [];
  const remoteResult = useMapData(remoteKeys);
  const [mockState, setMockState] = useState<IMockState>({
    data: null,
    isLoading: false,
    error: null,
  });

  useEffect(() => {
    if (data !== undefined || source !== "mock" || configError) {
      setMockState({
        data: null,
        isLoading: false,
        error: null,
      });
      return;
    }

    if (pageDataConfig?.mockData) {
      setMockState({
        data: pageDataConfig.mockData,
        isLoading: false,
        error: null,
      });
      return;
    }

    if (!pageDataConfig?.mockLoader) {
      return;
    }

    let cancelled = false;

    setMockState({
      data: null,
      isLoading: true,
      error: null,
    });

    pageDataConfig
      .mockLoader()
      .then(mockData => {
        if (cancelled) {
          return;
        }

        setMockState({
          data: mockData,
          isLoading: false,
          error: null,
        });
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }

        setMockState({
          data: null,
          isLoading: false,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [configError, data, pageDataConfig, source]);

  const resolvedData = useMemo(() => {
    if (data !== undefined) {
      return data;
    }
    if (source === "remote" && remoteResult.data) {
      // useMapData 以 dataKey（可能是完整 URL）为键返回数据。
      // 将 URL 键规范化为短别名（路径末段去 .json），便于组件按 data.list / data.detail_xxx 取用。
      const remapped: Record<string, any> = {};
      for (const [key, value] of Object.entries(remoteResult.data)) {
        const alias = /^(https?:)?\/\//.test(key)
          ? key.split("/").pop()?.replace(/\.json$/, "") ?? key
          : key;
        remapped[alias] = value;
      }
      return remapped;
    }
    return mockState.data;
  }, [data, source, remoteResult.data, mockState.data]);

  if (!page) {
    return renderState("页面未生成", effectivePathname);
  }

  const PageComponent = page.Component;
  const isLoading = data === undefined && (source === "remote" ? remoteResult.isLoading : mockState.isLoading);
  const error = data === undefined && source === "remote" ? remoteResult.error : mockState.error;

  if (configError === "mock") {
    return renderState("Mock 数据未配置", pageId);
  }

  if (configError === "remote") {
    return renderState("远端数据未配置", pageId);
  }

  // loadingPassthrough：loading/error 期间不拦截，直接渲染组件，
  // 由组件自行处理骨架屏与错误态，避免首屏全屏 loading 占位造成的白屏闪烁。
  if (pageDataConfig?.loadingPassthrough) {
    return <PageComponent data={resolvedData} />;
  }

  if (isLoading) {
    return renderState("数据加载中", pageId);
  }

  if (error) {
    return renderState("数据加载失败", error.message);
  }

  return <PageComponent data={resolvedData} />;
};

export default GeneratedPageHost;
